import type { Project } from "../types";
import type { ScenarioState } from "./scenario/types";

const STORAGE_KEY = "simulering-project";

/**
 * Saves the current scenario state to localStorage.
 * Can be used from components by passing the scenario state from useScenarioStore().
 */
export function saveProject(scenarioState: ScenarioState) {
	// Only save if in custom mode
	const hasCustomScenario = localStorage.getItem("hasCustomScenario");
	if (!hasCustomScenario) return;

	const project: Project = {
		meta: JSON.parse(JSON.stringify(scenarioState.meta)),
		agents: JSON.parse(JSON.stringify(scenarioState.agents)),
		clockAnnotations: JSON.parse(JSON.stringify(scenarioState.clockAnnotations)),
		speedControls: JSON.parse(JSON.stringify(scenarioState.speedControls)),
		analysis: JSON.parse(JSON.stringify(scenarioState.analysis)),
		viewportPins: JSON.parse(JSON.stringify(scenarioState.viewportPins)),
		autoPan: scenarioState.autoPan,
	};
	try {
		localStorage.setItem(STORAGE_KEY, JSON.stringify(project));
	} catch (e) {
		console.error("Failed to save project to localStorage:", e);
		console.error("Project size:", JSON.stringify(project).length, "bytes");
	}
}

/**
 * Loads a project from localStorage and returns it.
 * Returns null if no project is found or if loading fails.
 * The caller is responsible for applying the data to the store.
 */
export function loadProject(): Project | null {
	try {
		const raw = localStorage.getItem(STORAGE_KEY);
		if (!raw) return null;
		const project: any = JSON.parse(raw);

		// Migrate old projects without viewportPins field
		if (!project.viewportPins || project.viewportPins.length === 0) {
			project.viewportPins = [
				{
					id: 'viewport-first',
					time: "default-start",
					position: [59.33914127606005, 18.059999942779545],
					zoom: 18,
				}
			];
		}
		
		// Check for corrupted positions (0,0 coordinates)
		const hadCorruptedPin = project.viewportPins?.some((p: any) => p.position[0] === 0 && p.position[1] === 0);
		if (hadCorruptedPin) {
			// Replace with default
			project.viewportPins = [
				{
					id: 'viewport-first',
					time: "default-start",
					position: [59.33914127606005, 18.059999942779545],
					zoom: 18,
				}
			];
		}
		
		// Set autoPan default if not present
		if (project.autoPan === undefined) {
			project.autoPan = true;
		}

		return project as Project;
	} catch (e) {
		console.error("Failed to load project from localStorage:", e);
		if (e instanceof SyntaxError) {
			console.error("Invalid JSON in localStorage - may be corrupted");
		}
		return null;
	}
}

export function clearProject() {
	localStorage.removeItem(STORAGE_KEY);
	localStorage.removeItem("hasCustomScenario");
}

let saveTimeout: ReturnType<typeof setTimeout> | null = null;

/**
 * Schedules a debounced save operation.
 * Call this after making changes to scenario state.
 * The actual save will happen 500ms after the last call.
 */
export function scheduleSave(getScenarioState: () => ScenarioState) {
	if (saveTimeout) clearTimeout(saveTimeout);
	saveTimeout = setTimeout(() => saveProject(getScenarioState()), 500);
}
